import { useState, useCallback, useEffect, useMemo, CSSProperties } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import Divider from '@mui/material/Divider';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import Image from 'components/Image';
import { useGetDisplayObjects } from 'services/v1/Tenant/AROKMS/DisplayTableService';
import { DataInputObjectFile } from 'types/api/Tenant/AROKMS/DisplayTableTypes';
import { DATA_INPUT_OBJECT_TYPE } from 'constant/DataInputConstant';
import { getYoutubeThumbnailFromURL } from 'utils/String';
import { Box, Chip, Fade, Tooltip } from '@mui/material';
import ReactPlayer from 'react-player/lazy';
import { PDFViewer } from '../ModalAttachmentContent/PDFViewer';

// ─── Constants ───────────────────────────────────────────────────────────────

const TAB_ALL = 'ALL';

const SECTION_CONFIG = [
  {
    key: 'IMAGES_ATTACHMENT',
    label: 'Images',
    objectType: DATA_INPUT_OBJECT_TYPE.IMAGE,
    icon: ImageOutlinedIcon,
    color: '#6C63FF',
    bg: '#EDE9FF',
  },
  {
    key: 'DOCUMENTS_ATTACHMENT',
    label: 'Documents',
    objectType: DATA_INPUT_OBJECT_TYPE.DOCUMENT,
    icon: InsertDriveFileOutlinedIcon,
    color: '#E67E22',
    bg: '#FEF3E2',
  },
  {
    key: 'VIDEO_CONTENT',
    label: 'Videos',
    objectType: DATA_INPUT_OBJECT_TYPE.VIDEO,
    icon: VideoLibraryOutlinedIcon,
    color: '#E74C3C',
    bg: '#FDEDEC',
  },
  {
    key: 'EXTERNAL_LINKS',
    label: 'Links',
    objectType: DATA_INPUT_OBJECT_TYPE.LINK,
    icon: LinkOutlinedIcon,
    color: '#2980B9',
    bg: '#E8F4FD',
  },
] as const;

function getFileExtension(name: string): string {
  const parts = name.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Styles = {
  modalStyle: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '85%',
    maxWidth: 1100,
    bgcolor: 'background.paper',
    borderRadius: 2.5,
    px: 3,
    py: 2.5,
    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column' as const,
    maxHeight: '90vh',
  },
  scrollArea: {
    flex: 1,
    overflow: 'auto',
    px: 0.5,
    mt: 1.5,
    '&::-webkit-scrollbar': { width: 6 },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#C4C4C4', borderRadius: 3 },
  },
  tabBar: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap' as const,
    py: 1.5,
  },
  itemCard: {
    borderRadius: 2,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
    },
    '&:hover .overlay': {
      opacity: 1,
    },
  },
  boxOverlay: {
    opacity: 0,
    transition: 'opacity 0.25s ease',
    backdropFilter: 'blur(2px)',
  },
  imgStyle: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    display: 'block',
  } as CSSProperties,
  videoStyle: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    display: 'block',
  } as CSSProperties,
  contentBoxItem: {
    height: '100%',
    width: '100%',
    cursor: 'pointer',
    bgcolor: '#F8F9FB',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
  },
  sectionDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1.5,
    mt: 0.5,
  },
  emptyState: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    py: 4,
    color: '#A0AEC0',
    borderRadius: 2,
    border: '1px dashed #E2E8F0',
    bgcolor: '#FAFBFC',
  },
  viewerContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'auto',
    borderRadius: 2,
    bgcolor: '#F8F9FB',
    minHeight: 300,
  },
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface ModalPreviewAttachmentProps {
  visible?: boolean;
  onClose: () => void;
  onChange: (fieldName: string, value: string | null) => void;
  fileUrl: string;
  subjectId: string;
  fieldName: string;
  fieldLabel: string;
  initialObjectIds?: string[];
  viewOnly?: boolean;
}

interface PreviewAttachmentData {
  [key: string]: DataInputObjectFile[];
}

interface ViewContent {
  open: boolean;
  title: string;
  contentType: string | null;
  url: string;
  fromInitial: boolean;
}

const INITIAL_VIEW: ViewContent = {
  open: false,
  title: '',
  contentType: null,
  url: '',
  fromInitial: false,
};

// ─── Main Component ──────────────────────────────────────────────────────────

export function ModalDisplayAttachments(props: ModalPreviewAttachmentProps) {
  const { onClose, visible = false, subjectId, initialObjectIds, fieldLabel } = props;

  const [activeTab, setActiveTab] = useState<string>(TAB_ALL);
  const [viewContent, setViewContent] = useState<ViewContent>(INITIAL_VIEW);
  const [objectIds, setObjectIds] = useState<string[]>([]);
  const [previewData, setPreviewData] = useState<PreviewAttachmentData>({
    IMAGES_ATTACHMENT: [],
    DOCUMENTS_ATTACHMENT: [],
    VIDEO_CONTENT: [],
    EXTERNAL_LINKS: [],
  });

  const { data: objectsData, isLoading: isLoadingObjectData } = useGetDisplayObjects(objectIds, subjectId);

  // ── Derived ────────────────────────────────────────────────────────────────

  const totalAttachments = useMemo(() => {
    return (
      previewData.IMAGES_ATTACHMENT.length +
      previewData.DOCUMENTS_ATTACHMENT.length +
      previewData.VIDEO_CONTENT.length +
      previewData.EXTERNAL_LINKS.length
    );
  }, [previewData]);

  const sectionsToRender = useMemo(() => {
    if (activeTab === TAB_ALL) return SECTION_CONFIG;
    const found = SECTION_CONFIG.find((s) => s.objectType === activeTab);
    return found ? [found] : SECTION_CONFIG;
  }, [activeTab]);

  const isViewerOpen = viewContent.open && viewContent.contentType !== null;

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (initialObjectIds) {
      setObjectIds(initialObjectIds);
    }
  }, [initialObjectIds]);

  useEffect(() => {
    if (objectsData?.data && objectsData.data.length > 0) {
      setPreviewData({
        IMAGES_ATTACHMENT: objectsData.data.filter((item) => item.objectType === 'IMAGE'),
        DOCUMENTS_ATTACHMENT: objectsData.data.filter((item) => item.objectType === 'DOCUMENT'),
        VIDEO_CONTENT: objectsData.data.filter((item) => item.objectType === 'VIDEO'),
        EXTERNAL_LINKS: objectsData.data.filter((item) => item.objectType === 'LINK'),
      });

      const first = objectsData.data[0];
      if (first.objectType === DATA_INPUT_OBJECT_TYPE.LINK) {
        window.open(first.link, '_blank', 'noopener,noreferrer');
        return;
      }
      setViewContent({
        open: true,
        title: first.objectName || '',
        contentType: first.objectType,
        url: first.link || '',
        fromInitial: true,
      });
    }
  }, [objectsData?.data]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleCloseModal = () => {
    onClose();
    setObjectIds([]);
    setActiveTab(TAB_ALL);
    setViewContent(INITIAL_VIEW);
    setPreviewData({
      IMAGES_ATTACHMENT: [],
      DOCUMENTS_ATTACHMENT: [],
      VIDEO_CONTENT: [],
      EXTERNAL_LINKS: [],
    });
  };

  const handleBackToList = useCallback(() => {
    setViewContent(INITIAL_VIEW);
  }, []);

  const handleViewItem = (item: DataInputObjectFile) => {
    if (item.objectType === DATA_INPUT_OBJECT_TYPE.LINK) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
      return;
    }
    setViewContent({
      open: true,
      title: item.objectName,
      contentType: item.objectType,
      url: item.link,
      fromInitial: false,
    });
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderItemContent = (item: DataInputObjectFile) => {
    switch (item.objectType) {
      case 'IMAGE':
        return <img src={item.link} alt={item.objectName} style={Styles.imgStyle} />;
      case 'VIDEO':
        return (
          <Box position='relative' width='100%' height='100%'>
            <video
              src={`${item.link}#t=0.1`}
              poster={getYoutubeThumbnailFromURL(item.link)}
              preload='metadata'
              style={Styles.videoStyle}
            />
            <Box
              position='absolute'
              top='50%'
              left='50%'
              sx={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}
            >
              <PlayCircleOutlineIcon
                sx={{ fontSize: 36, color: 'rgba(255,255,255,0.85)', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }}
              />
            </Box>
          </Box>
        );
      case 'DOCUMENT': {
        const ext = getFileExtension(item.objectName);
        return (
          <Stack alignItems='center' sx={Styles.contentBoxItem} justifyContent='center' p={1.5} spacing={0.5}>
            <DescriptionIcon sx={{ fontSize: 32, color: '#E67E22' }} />
            {ext && (
              <Chip
                label={ext}
                size='small'
                sx={{ fontSize: 10, height: 18, bgcolor: '#FEF3E2', color: '#E67E22', fontWeight: 600 }}
              />
            )}
            <Tooltip title={item.objectName}>
              <Typography
                variant='input-label-gray'
                component='p'
                fontSize='11px'
                textAlign='center'
                textOverflow='ellipsis'
                overflow='hidden'
                whiteSpace='nowrap'
                width='100%'
                mt={0.5}
              >
                {item.objectName}
              </Typography>
            </Tooltip>
          </Stack>
        );
      }
      case 'LINK':
        return (
          <Stack alignItems='center' sx={Styles.contentBoxItem} justifyContent='center' p={1.5} spacing={0.5}>
            <LaunchOutlinedIcon sx={{ fontSize: 32, color: '#2980B9' }} />
            <Tooltip title={item.objectName}>
              <Typography
                variant='input-label-gray'
                component='p'
                fontSize='11px'
                textAlign='center'
                textOverflow='ellipsis'
                overflow='hidden'
                whiteSpace='nowrap'
                width='100%'
                mt={0.5}
              >
                {item.objectName}
              </Typography>
            </Tooltip>
          </Stack>
        );
      default:
        return null;
    }
  };

  const renderSection = (section: (typeof SECTION_CONFIG)[number], showHeader: boolean) => {
    const SectionIcon = section.icon;
    const items = previewData[section.key] || [];
    if (items.length === 0 && activeTab !== TAB_ALL) {
      return (
        <Box key={section.key} sx={Styles.emptyState}>
          <SectionIcon sx={{ fontSize: 28, color: '#CBD5E0', mb: 0.5 }} />
          <Typography variant='caption' sx={{ color: '#A0AEC0' }}>
            No {section.label.toLowerCase()} attached
          </Typography>
        </Box>
      );
    }
    if (items.length === 0) return null;

    return (
      <Stack key={section.key}>
        {showHeader && (
          <Box sx={Styles.sectionDivider}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 0.75,
                bgcolor: section.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <SectionIcon sx={{ fontSize: 14, color: section.color }} />
            </Box>
            <Typography variant='input-label-gray' component='h2' fontWeight='bold' fontSize={13}>
              {section.label}
            </Typography>
            <Chip
              label={items.length}
              size='small'
              sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: section.bg, color: section.color }}
            />
          </Box>
        )}
        <Box sx={Styles.gridContainer}>
          {items.map((item) => (
            <Box key={item.id || item.objectName} position='relative' sx={{ ...Styles.itemCard, aspectRatio: '1 / 1' }}>
              {renderItemContent(item)}
              <ContentOverlayAction objectName={item.objectName} onViewClick={() => handleViewItem(item)} />
            </Box>
          ))}
        </Box>
      </Stack>
    );
  };

  const renderViewer = () => {
    switch (viewContent.contentType) {
      case DATA_INPUT_OBJECT_TYPE.IMAGE:
        return (
          <Box sx={{ ...Styles.viewerContainer, height: '68vh' }}>
            <Image
              src={viewContent.url}
              alt={viewContent.title}
              showLoading={true}
              errorIcon={true}
              duration={1000}
              shiftDuration={300}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </Box>
        );
      case DATA_INPUT_OBJECT_TYPE.DOCUMENT:
        return (
          <Box sx={{ height: '68vh', overflow: 'auto', borderRadius: 2 }}>
            <PDFViewer url={viewContent.url} />
          </Box>
        );
      case DATA_INPUT_OBJECT_TYPE.VIDEO:
        return (
          <Box sx={{ ...Styles.viewerContainer, height: '68vh' }}>
            <ReactPlayer
              url={viewContent.url}
              width='100%'
              height='100%'
              controls
              playing
              volume={0.6}
              config={{ youtube: { embedOptions: { autoplay: 1, height: 400 } } }}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <Modal open={visible} sx={{ zIndex: 1000 }}>
      <Stack sx={Styles.modalStyle}>
        {/* ── Header ─────────────────────────────────────── */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' alignItems='center' gap={1.5}>
            {isViewerOpen && (
              <Tooltip title={viewContent.fromInitial ? 'View all attachments' : 'Back to list'}>
                <IconButton onClick={handleBackToList} size='small'>
                  <KeyboardBackspaceOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}
            <Stack>
              <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
                {isViewerOpen ? viewContent.title || 'Attachment' : fieldLabel}
              </Typography>
              {!isViewerOpen && !isLoadingObjectData && (
                <Typography variant='caption' sx={{ color: '#8895A7', mt: -0.3 }}>
                  {totalAttachments} {totalAttachments === 1 ? 'attachment' : 'attachments'}
                </Typography>
              )}
            </Stack>
          </Stack>
          <IconButton onClick={handleCloseModal} size='small'>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE', fontSize: 22 }} />
          </IconButton>
        </Stack>

        {/* ── Tab Bar (only in list view) ────────────────── */}
        {!isViewerOpen && !isLoadingObjectData && (
          <Box sx={Styles.tabBar}>
            <FilterTab
              label='All'
              count={totalAttachments}
              icon={<AppsOutlinedIcon sx={{ fontSize: 15 }} />}
              active={activeTab === TAB_ALL}
              activeColor='#3B4797'
              activeBg='#E8EAF6'
              onClick={() => setActiveTab(TAB_ALL)}
            />
            {SECTION_CONFIG.map((section) => {
              const SIcon = section.icon;
              return (
                <FilterTab
                  key={section.key}
                  label={section.label}
                  count={(previewData[section.key] || []).length}
                  icon={<SIcon sx={{ fontSize: 15 }} />}
                  active={activeTab === section.objectType}
                  activeColor={section.color}
                  activeBg={section.bg}
                  onClick={() => setActiveTab(section.objectType)}
                />
              );
            })}
          </Box>
        )}

        <Divider />

        {/* ── Body ───────────────────────────────────────── */}
        {isLoadingObjectData && (
          <Stack flex={1} justifyContent='center' alignItems='center' minHeight={300}>
            <ActivityIndicator />
          </Stack>
        )}

        {!isLoadingObjectData && !isViewerOpen && (
          <Fade in={!isViewerOpen} timeout={200}>
            <Stack sx={Styles.scrollArea}>
              <Stack spacing={activeTab === TAB_ALL ? 3 : 0}>
                {sectionsToRender.map((section) => renderSection(section, activeTab === TAB_ALL))}
              </Stack>
            </Stack>
          </Fade>
        )}

        {!isLoadingObjectData && isViewerOpen && (
          <Fade in={isViewerOpen} timeout={200}>
            <Stack sx={{ mt: 1.5, flex: 1, overflow: 'hidden' }}>{renderViewer()}</Stack>
          </Fade>
        )}
      </Stack>
    </Modal>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function FilterTab({
  label,
  count,
  icon,
  active,
  activeColor,
  activeBg,
  onClick,
}: {
  label: string;
  count: number;
  icon: React.ReactNode;
  active: boolean;
  activeColor: string;
  activeBg: string;
  onClick: () => void;
}) {
  return (
    <Button
      size='small'
      onClick={onClick}
      startIcon={icon}
      sx={{
        textTransform: 'none',
        borderRadius: 5,
        px: 1.5,
        py: 0.5,
        fontSize: 12,
        fontWeight: active ? 700 : 500,
        color: active ? activeColor : '#667085',
        bgcolor: active ? activeBg : 'transparent',
        border: active ? `1px solid ${activeColor}30` : '1px solid transparent',
        transition: 'all 0.15s ease',
        '&:hover': { bgcolor: active ? activeBg : '#F5F5F5' },
      }}
    >
      {label}
      <Chip
        label={count}
        size='small'
        sx={{
          ml: 0.75,
          height: 18,
          fontSize: 10,
          fontWeight: 700,
          bgcolor: active ? `${activeColor}20` : '#EAECF0',
          color: active ? activeColor : '#667085',
        }}
      />
    </Button>
  );
}

function ContentOverlayAction({ onViewClick, objectName }: { onViewClick: () => void; objectName?: string }) {
  return (
    <Box
      className='overlay'
      position='absolute'
      top={0}
      left={0}
      width='100%'
      height='100%'
      display='flex'
      justifyContent='center'
      alignItems='center'
      bgcolor='rgba(59, 71, 151, 0.65)'
      sx={Styles.boxOverlay}
    >
      <Tooltip title={`View ${objectName}`} placement='top'>
        <IconButton
          size='small'
          onClick={onViewClick}
          sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
